import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  api, CONDITION_GRADES, NO_COST_ORIGINS, WRITE_OFF_REASONS, isNoCostOrigin,
  type ItemDetail as Item, type WriteOffReason,
} from "../lib/api";
import { Sticker } from "../components/Sticker";
import { Field } from "../components/Field";
import { PhotoManager } from "../components/PhotoManager";
import { PlatformMark } from "../components/PlatformMark";
import { LabelPanel } from "../components/LabelPrint";
import { CrosslistPanel } from "../components/CrosslistPanel";
import { MeasurementsPanel } from "../components/MeasurementsPanel";
import { PackagingPanel } from "../components/PackagingPanel";
import { formatOz, toOz } from "../lib/weights";
import { hasMeasurements } from "../lib/measurements";
import { audienceLabel, conditionLabel, money, shortDate } from "../lib/format";
import { estimateProfit, parseFee, platformFee, type FeeProfile, type CostBasis } from "../lib/profit";
import { BUYER_LABEL, orderKey, saleRef } from "../lib/tieback";

const PLATFORMS = ["ebay", "poshmark", "mercari", "depop", "vinted", "facebook"] as const;
/** Channels where the seller can end up paying the label. */
const SELLER_SHIPPING_PLATFORMS: string[] = ["ebay", "poshmark"];
/** Poshmark's flat label, and the discounted rate it sometimes offers instead. */
const POSHMARK_SHIPPING = ["6.99", "4.99"];
/** A figure typed for another channel is not a Poshmark rate; fall back to the flat one. */
const poshmarkRate = (v: string) => (POSHMARK_SHIPPING.includes(v) ? v : POSHMARK_SHIPPING[0]);
const AUDIENCES = ["", "adult", "kids", "baby", "unisex_all"] as const;
const SEASONS = ["", "spring", "summer", "fall", "winter", "all_season"] as const;
// The lifecycle statuses you can set by hand. Damaged / donated / lost go
// through "Write off", which also ends listings and records the reason.
const STATUSES = ["draft", "in_stock", "listed", "sold", "shipped"] as const;
const CONDITIONS = ["", ...CONDITION_GRADES.map((c) => c.grade)] as const;
const CONDITION_OPTION_LABEL: Record<string, string> = Object.fromEntries(
  CONDITION_GRADES.map((c) => [c.grade, c.label])
);

// Fields whose change makes the comps and price stale.
const REPRICE_TRIGGERS = new Set(["title", "brand", "category", "subcategory", "sizeLabel", "purchasePrice"]);

// Every stored field, in display order. `ai: true` marks fields the vision
// pipeline derives from the photos (shown with a badge when the item was
// AI-catalogued); the rest are manual or system fields.
const RECORD_FIELDS: Array<{ key: string; label: string; ai?: boolean }> = [
  { key: "sku", label: "SKU" },
  { key: "title", label: "Title", ai: true },
  { key: "brand", label: "Brand", ai: true },
  { key: "category", label: "Category", ai: true },
  { key: "subcategory", label: "Subcategory", ai: true },
  { key: "audience", label: "Audience", ai: true },
  { key: "season", label: "Season", ai: true },
  { key: "sizeLabel", label: "Size", ai: true },
  { key: "itemWeightDisplay", label: "Item weight" },
  { key: "packagingDisplay", label: "Packaging" },
  { key: "measurements", label: "Measurements" },
  { key: "colorway", label: "Color", ai: true },
  { key: "materials", label: "Materials", ai: true },
  { key: "styleKeywords", label: "Style keywords", ai: true },
  { key: "conditionGrade", label: "Condition", ai: true },
  { key: "conditionNotes", label: "Condition notes", ai: true },
  { key: "listingDescription", label: "Description", ai: true },
  { key: "tagPrice", label: "Thrift tag price", ai: true },
  { key: "purchasePrice", label: "Purchase price" },
  { key: "purchaseDate", label: "Purchased" },
  { key: "store", label: "Found at" },
  { key: "isPersonal", label: "Personal" },
  { key: "storageBin", label: "Storage bin" },
  { key: "status", label: "Status" },
  { key: "enrichmentStatus", label: "Enrichment" },
  { key: "createdAt", label: "Created" },
  { key: "updatedAt", label: "Updated" },
];

// Fields the user can edit, with the input type to render, grouped so the
// form reads top-down: where it came from first, then what it is, then
// condition, then housekeeping. `optionLabel` maps a select value to its text.
type EditType = "text" | "number" | "date" | "textarea" | "csv" | "select";
type EditGroup = "origin" | "catalog" | "condition" | "housekeeping";
const EDITABLE: Array<{
  key: string; label: string; type: EditType; group: EditGroup;
  options?: readonly string[]; optionLabel?: Record<string, string>;
}> = [
  { key: "purchasePrice", label: "Purchase price", type: "number", group: "origin" },
  { key: "purchaseDate", label: "Purchased (YYYY-MM-DD)", type: "date", group: "origin" },
  { key: "tagPrice", label: "Thrift tag price", type: "number", group: "origin" },
  { key: "title", label: "Title", type: "text", group: "catalog" },
  { key: "brand", label: "Brand", type: "text", group: "catalog" },
  { key: "category", label: "Category", type: "text", group: "catalog" },
  { key: "subcategory", label: "Subcategory", type: "text", group: "catalog" },
  { key: "audience", label: "Audience", type: "select", group: "catalog", options: AUDIENCES },
  { key: "season", label: "Season", type: "select", group: "catalog", options: SEASONS },
  { key: "sizeLabel", label: "Size", type: "text", group: "catalog" },
  { key: "colorway", label: "Color", type: "text", group: "catalog" },
  { key: "materials", label: "Materials (comma-separated)", type: "csv", group: "catalog" },
  { key: "styleKeywords", label: "Style keywords (comma-separated)", type: "csv", group: "catalog" },
  // The listing copy (attributes.listingDescription): what the Depop export
  // uses, and a sync import's original marketplace text.
  { key: "listingDescription", label: "Description", type: "textarea", group: "catalog" },
  {
    key: "conditionGrade", label: "Condition", type: "select", group: "condition",
    options: CONDITIONS, optionLabel: CONDITION_OPTION_LABEL,
  },
  { key: "conditionNotes", label: "Condition notes", type: "textarea", group: "condition" },
  { key: "storageBin", label: "Storage bin", type: "text", group: "housekeeping" },
  { key: "status", label: "Status", type: "select", group: "housekeeping", options: STATUSES },
];
const GROUP_TITLE: Record<EditGroup, string> = {
  origin: "Where it came from",
  catalog: "What it is",
  condition: "Condition",
  housekeeping: "Housekeeping",
};
const GROUPS: EditGroup[] = ["origin", "catalog", "condition", "housekeeping"];

/**
 * A field's stored value. The description lives in attributes rather than a
 * column; everything else is top-level on the item.
 */
function fieldValue(item: Item, key: string): unknown {
  if (key === "listingDescription") return item.attributes?.listingDescription ?? null;
  // Packaging details (edited in their own panel): weights show as pounds and ounces.
  if (key === "itemWeightDisplay") return formatOz(toOz(item.attributes?.itemWeight, item.attributes?.itemWeightUnit));
  if (key === "packagingDisplay") return packagingSummary(item.attributes);
  if (key === "measurements") return measurementSummary(item.measurements);
  return (item as unknown as Record<string, unknown>)[key];
}

function packagingSummary(a: Record<string, unknown> | null | undefined): string | null {
  const kind = a?.packagingType === "box" ? "Box" : a?.packagingType === "polybag" ? "Polybag" : null;
  const d = a?.packageDims as { length?: number | null; width?: number | null; height?: number | null } | null | undefined;
  const dims = d ? [d.length, d.width, d.height].filter((v): v is number => typeof v === "number") : [];
  const size = dims.length ? `${dims.join(" × ")} in` : null;
  return [kind, size].filter(Boolean).join(" · ") || null;
}

const MEASURE_LABEL: Record<string, string> = {
  pit_to_pit: "pit to pit", length: "length", sleeve: "sleeve", shoulder: "shoulder", waist: "waist",
  inseam: "inseam", rise: "rise", hip: "hip", leg_opening: "leg opening",
};
function measurementSummary(m: Record<string, unknown> | null | undefined): string | null {
  if (!hasMeasurements(m)) return null;
  const unit = m!.unit === "cm" ? " cm" : "″";
  const parts = Object.entries(m!).filter(([, v]) => typeof v === "number").map(([k, v]) => `${MEASURE_LABEL[k] ?? k} ${v}${unit}`);
  return [m!.garment === "bottom" ? "Bottoms" : "Top", ...parts].join(" · ");
}

/** Created / updated stamps, in Eastern time with EST or EDT as it applies. */
const EASTERN = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  year: "numeric", month: "short", day: "numeric",
  hour: "numeric", minute: "2-digit", timeZoneName: "short",
});
function fmtEastern(v: unknown): string {
  if (v == null || v === "") return "—";
  const d = new Date(String(v));
  return Number.isNaN(d.getTime()) ? String(v) : EASTERN.format(d);
}

function fmtValue(v: unknown): string {
  if (v == null || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

export function ItemDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [item, setItem] = useState<Item | null>(null);
  const [photoUrls, setPhotoUrls] = useState<
    Array<{ id: string; url: string; kind: string; slot: string | null }>
  >([]);
  const [error, setError] = useState<string | null>(null);
  const [listPrice, setListPrice] = useState("");
  const [platform, setPlatform] = useState<string>("ebay");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [showListing, setShowListing] = useState(false);
  const [showSale, setShowSale] = useState(false);
  const [salePlatform, setSalePlatform] = useState<string>("ebay");
  const [salePrice, setSalePrice] = useState("");
  const [saleChoice, setSaleChoice] = useState<string>("other"); // listing id, or "other"
  // Tie-back fields: the date the marketplace says it sold, and the buyer handle
  // its report identifies the order by. Together they reconcile against a CSV.
  const [saleSoldOn, setSaleSoldOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [saleBuyer, setSaleBuyer] = useState("");
  // What it shipped in. Bubble costs more than poly, and the charge lands once
  // per shipment, so a bundle's second item adds nothing.
  const [salePackaging, setSalePackaging] = useState<"poly" | "bubble" | "other" | "none">("poly");
  // Depop Boost adds the platform's boost % to the fee; on eBay and Poshmark the
  // seller may pay the label, which is deducted as a shipping cost.
  const [saleBoosted, setSaleBoosted] = useState(false);
  const [salePaysShipping, setSalePaysShipping] = useState(false);
  const [saleShipCost, setSaleShipCost] = useState("");
  // Store and personal live outside the generic EDITABLE loop: one has to be
  // clearable (which the loop skips) and the other is a boolean.
  const [draftStore, setDraftStore] = useState("");
  const [draftPersonal, setDraftPersonal] = useState(false);
  // "store" = bought somewhere (the store field applies); otherwise one of the
  // fixed no-cost origins, which zero the purchase price.
  const [draftOrigin, setDraftOrigin] = useState<string>("store");
  const [stores, setStores] = useState<string[]>([]);
  const [costDraft, setCostDraft] = useState<Record<string, string>>({});
  const [fees, setFees] = useState<FeeProfile[]>([]);
  const [costEnv, setCostEnv] = useState<Omit<CostBasis, "purchasePrice"> | null>(null);
  const [mailer, setMailer] = useState<Record<string, number> | null>(null);
  // Write-off form (damaged / donated / lost).
  const [showWriteOff, setShowWriteOff] = useState(false);
  const [writeOffReason, setWriteOffReason] = useState<WriteOffReason>("damaged");
  const [writeOffNote, setWriteOffNote] = useState("");
  const [showLabel, setShowLabel] = useState(false);
  const [showMeasure, setShowMeasure] = useState(false);
  const [showPackaging, setShowPackaging] = useState(false);
  // Prompts to refresh: photos were added after cataloguing, or a field that
  // the price depends on was edited. Each run is a paid call, so it is
  // offered rather than fired.
  const [photosChanged, setPhotosChanged] = useState(false);
  const [repriceOffered, setRepriceOffered] = useState(false);

  // Cost profiles + shared cost assumptions drive every profit estimate.
  useEffect(() => {
    void (async () => {
      try {
        const s = await api.get<{
          settings: Record<string, unknown>;
          platformFees: Array<Record<string, unknown>>;
        }>("/settings");
        setFees((s.platformFees ?? []).map(parseFee));
        setCostEnv({
          packaging: Number(s.settings?.avgPackagingCost ?? 0),
          shipping: Number(s.settings?.avgShippingCost ?? 0),
          buyerPaysShipping: !!s.settings?.buyerPaysShipping,
        });
        setMailer({
          poly: Number(s.settings?.polyMailerCost ?? 0),
          bubble: Number(s.settings?.bubbleMailerCost ?? 0),
          other: Number(s.settings?.avgPackagingCost ?? 0),
          none: 0,
        });
      } catch { /* estimates just won't render */ }
    })();
    void (async () => {
      try {
        const r = await api.get<{ sources: Array<{ name: string }> }>("/sources");
        setStores(r.sources.map((s) => s.name));
      } catch { /* the store field still accepts free text */ }
    })();
  }, []);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const [detail, photos] = await Promise.all([
        api.get<Item>(`/items/${id}`),
        api.get<{ photos: Array<{ id: string; url: string; kind: string; slot: string | null }> }>(`/items/${id}/photos`),
      ]);
      setItem(detail);
      setPhotoUrls(photos.photos);
      if (!listPrice && detail.marketProfile?.suggestedList) {
        setListPrice(detail.marketProfile.suggestedList);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load this item");
    }
  }, [id, listPrice]);

  useEffect(() => { void load(); }, [id]);

  // While the pipeline runs, poll. It usually finishes inside a minute.
  useEffect(() => {
    if (!item) return;
    if (!["pending", "running"].includes(item.enrichmentStatus)) return;
    const t = setInterval(() => void load(), 6000);
    return () => clearInterval(t);
  }, [item?.enrichmentStatus, load]);

  function startEdit() {
    if (!item) return;
    const d: Record<string, string> = {};
    for (const f of EDITABLE) {
      const v = fieldValue(item, f.key);
      d[f.key] = f.type === "csv"
        ? (Array.isArray(v) ? v.join(", ") : "")
        : f.type === "date"
          ? (v == null ? "" : String(v).slice(0, 10)) // date input needs YYYY-MM-DD
          : v == null ? "" : String(v);
    }
    setDraft(d);
    // "Unknown" is the display fallback for an unattributed item, not a store
    // name — don't seed the field with it.
    const fromNoCost = isNoCostOrigin(item.store);
    setDraftOrigin(fromNoCost ? (item.store as string) : "store");
    setDraftStore(item.store && item.store !== "Unknown" && !fromNoCost ? item.store : "");
    setDraftPersonal(item.isPersonal);
    setEditing(true);
  }

  async function saveEdit() {
    if (!id || !item) return;
    setBusy(true);
    setError(null);
    try {
      const patch: Record<string, unknown> = {};
      for (const f of EDITABLE) {
        const rawVal = (draft[f.key] ?? "").trim();
        if (f.type === "csv") {
          patch[f.key] = rawVal ? rawVal.split(",").map((s) => s.trim()).filter(Boolean) : [];
        } else if (f.type === "number") {
          if (rawVal !== "") patch[f.key] = Number(rawVal);
        } else if (f.key === "conditionGrade") {
          // A closed list, so the blank option is a real "unset".
          patch[f.key] = rawVal || null;
        } else if (f.key === "listingDescription") {
          // Emptying the description hands it back to the generator: the API
          // rebuilds the posting from the item's fields.
          patch[f.key] = rawVal || null;
        } else if (rawVal !== "") {
          patch[f.key] = rawVal;
        }
      }
      // Sent unconditionally so both can be cleared/unset, unlike the loop above
      // which skips empty values.
      if (draftOrigin === "store") {
        patch.storeName = draftStore.trim() || null;
      } else {
        // From the family's closet or a gift: the origin is the store, and
        // there is no cost basis.
        patch.storeName = draftOrigin;
        patch.purchasePrice = 0;
      }
      patch.isPersonal = draftPersonal;

      // Only send what actually changed, so provenance (which fields were set
      // by hand) records real edits rather than every field on the form.
      const same = (a: unknown, b: unknown) =>
        JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
      const normalised = (k: string): unknown => {
        const v = fieldValue(item, k);
        if (k === "purchaseDate") return v == null ? null : String(v).slice(0, 10);
        // An empty list and no list are the same thing; sending [] for a null
        // column would otherwise pin the field empty against the vision step.
        if (Array.isArray(patch[k]) && v == null) return [];
        if (typeof v === "string" && /^-?\d+(\.\d+)?$/.test(v) && typeof patch[k] === "number") return Number(v);
        return v ?? null;
      };
      let repriceWorthy = false;
      for (const k of Object.keys(patch)) {
        if (k === "storeName") { if (same(patch[k], item.store === "Unknown" ? null : item.store)) delete patch[k]; continue; }
        if (same(patch[k], normalised(k))) delete patch[k];
        else if (REPRICE_TRIGGERS.has(k)) repriceWorthy = true;
      }

      if (Object.keys(patch).length > 0) await api.patch(`/items/${id}`, patch);
      setEditing(false);
      await load();
      if (repriceWorthy && item.marketProfile) setRepriceOffered(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save your changes");
    } finally {
      setBusy(false);
    }
  }

  /**
   * Run the pipeline again. `full` re-reads the photos (fields you set by hand
   * are pinned and shown to the model as facts); `reprice` skips the photos
   * and rebuilds comps + price from the fields as they stand.
   */
  async function rerun(mode: "full" | "reprice") {
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/items/${id}/enrich`, { mode });
      setPhotosChanged(false);
      setRepriceOffered(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start a refresh");
    } finally {
      setBusy(false);
    }
  }

  async function submitWriteOff() {
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/items/${id}/write-off`, {
        reason: writeOffReason,
        ...(writeOffNote.trim() ? { note: writeOffNote.trim() } : {}),
      });
      setShowWriteOff(false);
      setWriteOffNote("");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not write this item off");
    } finally {
      setBusy(false);
    }
  }

  /** Correct a recorded cost — the packaging figure is written for you, so it
   *  has to be fixable when the guess was wrong. */
  async function saveCost(costId: string) {
    const raw = (costDraft[costId] ?? "").trim();
    const amount = Number(raw);
    if (!Number.isFinite(amount) || amount < 0) { setError("Enter a valid amount"); return; }
    setBusy(true);
    setError(null);
    try {
      // Typing the real number means it is no longer an estimate.
      await api.patch(`/costs/${costId}`, { amount, isEstimate: false });
      setCostDraft((d) => { const n = { ...d }; delete n[costId]; return n; });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save that cost");
    } finally {
      setBusy(false);
    }
  }

  async function removeCost(costId: string) {
    if (!window.confirm("Remove this cost from the item?")) return;
    setBusy(true);
    setError(null);
    try {
      await api.del(`/costs/${costId}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not remove that cost");
    } finally {
      setBusy(false);
    }
  }

  async function toggleArchive() {
    if (!id || !item) return;
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/items/${id}`, {
        archivedAt: item.archivedAt ? null : new Date().toISOString(),
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update archive state");
    } finally {
      setBusy(false);
    }
  }

  async function removeItem() {
    if (!id) return;
    if (!window.confirm("Delete this item permanently? This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.del(`/items/${id}`);
      navigate("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete this item");
      setBusy(false);
    }
  }

  async function recordListing() {
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/items/${id}/listings`, {
        platform,
        listPrice: Number(listPrice),
        acceptsOffers: true,
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save that listing");
    } finally {
      setBusy(false);
    }
  }

  // Open the sold dialog, defaulting to the active listing (or the first one).
  function openSale() {
    const preferred = item?.listings.find((l) => l.status === "active") ?? item?.listings[0];
    if (preferred) {
      setSaleChoice(preferred.id);
      setSalePlatform(preferred.platform);
      setSalePrice(String(preferred.listPrice ?? ""));
    } else {
      setSaleChoice("other");
      setSalePlatform("ebay");
      setSalePrice(String(item?.marketProfile?.suggestedList ?? ""));
    }
    // The mailer defaults from the item's packaging: a box is "other", a polybag a poly mailer.
    setSalePackaging(item?.attributes?.packagingType === "box" ? "other" : "poly");
    setSaleBoosted(false);
    setSalePaysShipping(false);
    setSaleShipCost("");
    setShowListing(false);
    setShowSale(true);
  }

  // Selecting a listing prefills its platform + asking price as the sale price.
  function pickSale(choice: string) {
    setSaleChoice(choice);
    if (choice !== "other") {
      const l = item?.listings.find((x) => x.id === choice);
      if (l) { setSalePlatform(l.platform); setSalePrice(String(l.listPrice ?? "")); }
    }
  }

  /** The seller-paid shipping to deduct, or null when the buyer covers it. */
  function sellerShipCost(plat: string): number | null {
    if (!SELLER_SHIPPING_PLATFORMS.includes(plat) || !salePaysShipping) return null;
    return Number(plat === "poshmark" ? poshmarkRate(saleShipCost) : saleShipCost);
  }

  async function submitSale() {
    if (!id || !item) return;
    const price = Number(salePrice);
    if (!Number.isFinite(price) || price <= 0) { setError("Enter a valid sale price"); return; }
    const chosen = saleChoice !== "other" ? item.listings.find((l) => l.id === saleChoice) : null;
    const plat = chosen ? chosen.platform : salePlatform;
    const shipCost = sellerShipCost(plat);
    if (shipCost !== null && (!Number.isFinite(shipCost) || shipCost <= 0)) {
      setError("Enter what you paid for shipping, or untick it"); return;
    }
    setBusy(true);
    setError(null);
    try {
      const key = orderKey(plat, saleBuyer, saleSoldOn);
      await api.post(`/items/${id}/sales`, {
        platform: plat,
        salePrice: price,
        listingId: chosen ? chosen.id : undefined,
        // Noon UTC so the date renders the same everywhere, matching how the
        // reconciler stamps report dates.
        soldAt: `${saleSoldOn}T12:00:00.000Z`,
        packaging: salePackaging,
        boosted: plat === "depop" && saleBoosted,
        ...(shipCost !== null ? { shippingCost: shipCost } : {}),
        ...(key ? { externalOrderId: saleRef(key, item.sku) } : {}),
      });
      await load();
      setShowSale(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not record that sale");
    } finally {
      setBusy(false);
    }
  }

  async function editListingPrice(listingId: string, current: string | null) {
    const v = window.prompt("New asking price", current ?? "");
    if (v == null) return;
    const n = Number(v);
    if (!Number.isFinite(n) || n < 0) { setError("Enter a valid price"); return; }
    setBusy(true);
    try {
      await api.patch(`/listings/${listingId}`, { listPrice: n });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update that listing");
    } finally {
      setBusy(false);
    }
  }

  async function archiveListing(listingId: string) {
    setBusy(true);
    try {
      await api.patch(`/listings/${listingId}`, { archivedAt: new Date().toISOString() });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not archive that listing");
    } finally {
      setBusy(false);
    }
  }

  async function deleteListing(listingId: string) {
    if (!window.confirm("Delete this listing record permanently?")) return;
    setBusy(true);
    try {
      await api.del(`/listings/${listingId}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete that listing");
    } finally {
      setBusy(false);
    }
  }

  if (!item) {
    return (
      <div className="shell">
        {error ? <div className="notice">{error}</div> : <p className="hint">Loading</p>}
      </div>
    );
  }

  const pending = ["pending", "running"].includes(item.enrichmentStatus);
  const aiDerived = item.photos.length > 0 && ["complete", "partial"].includes(item.enrichmentStatus);
  const notSold = item.status !== "sold" && item.status !== "shipped";
  const writtenOff = ["damaged", "donated", "lost"].includes(item.status);
  const fromNoCost = isNoCostOrigin(item.store);
  // Fields the seller set by hand — pinned against the vision step.
  const manualFields = new Set<string>(
    Array.isArray(item.attributes?.manualFields) ? item.attributes.manualFields : []
  );
  // Hero thumbnail: the flat-front shot, falling back to any garment/first photo.
  const frontThumb =
    photoUrls.find((p) => p.slot === "flat_front") ??
    photoUrls.find((p) => p.kind === "item") ??
    photoUrls[0] ??
    null;

  // Profit estimates. Cost basis = what you paid + packaging (+ shipping when
  // you cover it). Reference sell price = suggested list, else the best listing.
  const basis: CostBasis | null = costEnv
    ? { ...costEnv, purchasePrice: Number(item.purchasePrice ?? 0) }
    : null;
  const feeFor = (p: string) => fees.find((f) => f.platform === p) ?? null;

  // Live preview while recording a sale, so an item price can be sanity-checked
  // against the payout the marketplace will actually show.
  const saleTiePlatform =
    saleChoice !== "other"
      ? (item.listings.find((l) => l.id === saleChoice)?.platform ?? salePlatform)
      : salePlatform;
  const saleTieKey = orderKey(saleTiePlatform, saleBuyer, saleSoldOn);
  const saleTieRef = saleTieKey ? saleRef(saleTieKey, item.sku) : null;
  const saleFeeProfile = feeFor(saleTiePlatform);
  const saleShipPreview = sellerShipCost(saleTiePlatform);
  const saleFeePreview = (() => {
    const price = Number(salePrice);
    if (!saleFeeProfile || !Number.isFinite(price) || price <= 0) return null;
    const boost =
      saleTiePlatform === "depop" && saleBoosted ? price * (saleFeeProfile.boostPct / 100) : 0;
    const fee = platformFee(price, saleFeeProfile) + boost;
    const ship = saleShipPreview !== null && Number.isFinite(saleShipPreview) ? saleShipPreview : 0;
    // An explicit shipping figure replaces the settings-average estimate.
    const profit = !basis
      ? 0
      : saleShipPreview !== null
        ? estimateProfit(price, { ...saleFeeProfile, sellerPaysShipping: false },
            { ...basis, buyerPaysShipping: true }) - boost - ship
        : estimateProfit(price, saleFeeProfile, basis) - boost;
    return { fee, boost, ship, net: price - fee - ship, profit };
  })();

  const listingPrices = item.listings.map((l) => Number(l.listPrice ?? 0)).filter((n) => n > 0);
  const refPrice =
    Number(item.marketProfile?.suggestedList ?? 0) ||
    (listingPrices.length ? Math.max(...listingPrices) : 0);
  // Average estimated profit across selling sites (excluding no-fee local pickup).
  const sellingFees = fees.filter((f) => f.platform !== "local");
  const estAvgProfit =
    basis && sellingFees.length && refPrice > 0
      ? sellingFees.reduce((s, f) => s + estimateProfit(refPrice, f, basis), 0) / sellingFees.length
      : null;
  const realizedProfit = item.ledger?.netProfit != null ? Number(item.ledger.netProfit) : null;
  // The sale that stands (newest first from the API); a returned one no longer counts.
  const standingSale = item.sales.find((s) => !s.returned) ?? null;

  return (
    <div className="shell">
      <header className="masthead">
        <span className="wordmark">{item.sku}</span>
        <Sticker status={item.status} />
      </header>

      {error && <div className="notice">{error}</div>}

      {item.archivedAt && (
        <div className="notice">
          This item is archived. It's hidden from your inventory list.
        </div>
      )}

      {pending && (
        <div className="notice ok">
          Reading the photos and pulling comps. This screen updates on its own.
        </div>
      )}

      {item.enrichmentStatus === "failed" && (
        <div className="notice">
          {item.enrichmentError ?? "Pricing failed."}{" "}
          <button className="quiet" onClick={() => void rerun("full")} disabled={busy}>
            Try again
          </button>
        </div>
      )}

      {writtenOff && (
        <div className="notice">
          Written off as {item.status}. It is out of your inventory and what you paid counts
          against profit.{" "}
          <button
            className="quiet"
            disabled={busy}
            onClick={() => void api.patch(`/items/${item.id}`, { status: "in_stock" }).then(load)}
          >
            Put it back in stock
          </button>
        </div>
      )}

      {photosChanged && !pending && (
        <div className="notice ok">
          Photos changed. Re-read them to update the catalog details and price?{" "}
          <button className="quiet" onClick={() => void rerun("full")} disabled={busy}>Re-read photos</button>{" "}
          <button className="quiet" onClick={() => setPhotosChanged(false)} disabled={busy}>Not now</button>
        </div>
      )}

      {repriceOffered && !pending && (
        <div className="notice ok">
          Details that the price depends on changed. Refresh the comps and suggested price?{" "}
          <button className="quiet" onClick={() => void rerun("reprice")} disabled={busy}>Refresh pricing</button>{" "}
          <button className="quiet" onClick={() => setRepriceOffered(false)} disabled={busy}>Not now</button>
        </div>
      )}

      <h1 style={{ marginBottom: "0.25rem" }}>{item.title ?? "Untitled find"}</h1>
      <p className="tag-meta" style={{ marginTop: 0 }}>
        {[
          item.brand, item.category, item.sizeLabel, audienceLabel(item.audience),
          conditionLabel(item.conditionGrade), item.colorway, item.season,
        ].filter(Boolean).join(" · ")}
      </p>
      <p className="tag-meta" style={{ marginTop: "0.15rem" }}>
        {fromNoCost ? (
          <span className="personal-flag closet-flag">{item.store}</span>
        ) : (
          <span>Found at {item.store && item.store !== "Unknown" ? item.store : "— (not recorded)"}</span>
        )}
        {item.isPersonal && <span className="personal-flag">personal</span>}
      </p>
      {aiDerived && (
        <p className="ai-note">
          <span className="ai-badge">✨ AI</span> Catalog details were read from your photos.
          {manualFields.size > 0 && ` ${manualFields.size} field${manualFields.size === 1 ? "" : "s"} set by hand and pinned.`}
        </p>
      )}

      <div className="detail-top">
        {frontThumb && (
          <span className="detail-hero">
            <img src={frontThumb.url} alt="Garment, flat front" />
          </span>
        )}
        <div className="actions detail-actions">
          <button className="ghost" onClick={startEdit} disabled={busy || editing}>Edit</button>
          <button className="ghost" onClick={() => setShowLabel((v) => !v)}>Print label</button>
          <button className="ghost" onClick={() => setShowMeasure((v) => !v)}>Measurements</button>
          <button className="ghost" onClick={() => setShowPackaging((v) => !v)}>Packaging details</button>
          <button className="ghost" onClick={() => void toggleArchive()} disabled={busy}>
            {item.archivedAt ? "Unarchive" : "Archive"}
          </button>
          {notSold && !writtenOff && (
            <button
              className="ghost"
              onClick={() => { setShowWriteOff((v) => !v); setShowSale(false); setShowListing(false); }}
              disabled={busy}
            >
              Write off
            </button>
          )}
          <button className="quiet danger" onClick={() => void removeItem()} disabled={busy}>
            Delete
          </button>
          {!pending && item.photos.length > 0 && (
            <button className="ghost" onClick={() => void rerun("full")} disabled={busy} title="Read the photos again and re-price. Fields you set by hand are kept.">
              Re-read photos
            </button>
          )}
          {!pending && item.marketProfile && (
            <button className="ghost" onClick={() => void rerun("reprice")} disabled={busy} title="Rebuild comps and the suggested price from the details as they stand.">
              Refresh pricing
            </button>
          )}
          {notSold && !writtenOff && (
            <button
              className="ghost"
              onClick={() => { setShowListing((v) => !v); setShowSale(false); setShowWriteOff(false); }}
              disabled={busy}
            >
              Record Listing
            </button>
          )}
          {notSold && !writtenOff && (
            <button onClick={openSale} disabled={busy}>Mark as Sold</button>
          )}
        </div>
      </div>

      {showLabel && <LabelPanel item={item} onClose={() => setShowLabel(false)} />}
      {showMeasure && <MeasurementsPanel item={item} onClose={() => setShowMeasure(false)} onSaved={load} />}
      {showPackaging && <PackagingPanel item={item} onClose={() => setShowPackaging(false)} onSaved={load} />}

      {showWriteOff && notSold && !writtenOff && (
        <section className="rail">
          <h2 className="eyebrow rail-label">Write off — remove from inventory</h2>
          <div className="sale-choices">
            {WRITE_OFF_REASONS.map((r) => (
              <label key={r.reason} className={`sale-choice ${writeOffReason === r.reason ? "active" : ""}`}>
                <input
                  type="radio" name="writeOffReason" value={r.reason}
                  checked={writeOffReason === r.reason}
                  onChange={() => setWriteOffReason(r.reason)}
                />
                <span className="sale-choice-body">
                  <span className="sale-choice-title">{r.label}</span>
                  <span className="tag-meta">{r.hint}</span>
                </span>
              </label>
            ))}
          </div>
          <div className="row" style={{ marginTop: "0.75rem" }}>
            <Field label="What happened" hint="Optional — kept with the item's condition notes">
              <input
                type="text"
                placeholder="e.g. bleach stain found on the hem"
                value={writeOffNote}
                onChange={(e) => setWriteOffNote(e.target.value)}
              />
            </Field>
          </div>
          <div className="actions">
            <button onClick={() => void submitWriteOff()} disabled={busy}>
              {busy ? "Saving" : `Mark ${writeOffReason}`}
            </button>
            <button className="quiet" onClick={() => setShowWriteOff(false)} disabled={busy}>Cancel</button>
          </div>
          <p className="hint">
            The item leaves your on-hand inventory, any live listings are ended, and what you paid
            ({money(item.purchasePrice)}) is counted against total profit. Reversible from this page.
          </p>
        </section>
      )}

      {showSale && notSold && (
        <section className="rail">
          <h2 className="eyebrow rail-label">Mark sold — which listing?</h2>

          <div className="sale-choices">
            {item.listings.map((l) => (
              <label key={l.id} className={`sale-choice ${saleChoice === l.id ? "active" : ""}`}>
                <input
                  type="radio" name="saleChoice" value={l.id}
                  checked={saleChoice === l.id}
                  onChange={() => pickSale(l.id)}
                />
                <span className="sale-choice-body">
                  <span className="sale-choice-title">{l.platform}</span>
                  <span className="tag-meta">
                    <Sticker status={l.status} /> listed {money(l.listPrice)}
                  </span>
                </span>
              </label>
            ))}
            <label className={`sale-choice ${saleChoice === "other" ? "active" : ""}`}>
              <input
                type="radio" name="saleChoice" value="other"
                checked={saleChoice === "other"}
                onChange={() => pickSale("other")}
              />
              <span className="sale-choice-body">
                <span className="sale-choice-title">Other channel</span>
                <span className="tag-meta">Sold somewhere not listed here</span>
              </span>
            </label>
          </div>

          <div className="row" style={{ marginTop: "0.75rem" }}>
            {saleChoice === "other" && (
              <Field label="Channel">
                <select value={salePlatform} onChange={(e) => setSalePlatform(e.target.value)}>
                  {PLATFORMS.map((p) => (<option key={p} value={p}>{p}</option>))}
                </select>
              </Field>
            )}
            <Field
              label="Item price"
              hint="What the buyer paid for the item — not your payout"
            >
              <input
                className="money" type="number" step="0.01" min="0"
                value={salePrice} onChange={(e) => setSalePrice(e.target.value)}
              />
            </Field>
            <Field label="Sold on" hint="The date on the marketplace, not today">
              <input
                type="date"
                value={saleSoldOn}
                onChange={(e) => setSaleSoldOn(e.target.value)}
              />
            </Field>
          </div>

          <div className="row">
            <Field
              label={BUYER_LABEL[saleTiePlatform] ?? "Buyer username"}
              hint="Optional, but it is what ties this sale to the sales report"
            >
              <input
                type="text" autoCapitalize="none" spellCheck={false}
                placeholder="e.g. obiwh_948"
                value={saleBuyer} onChange={(e) => setSaleBuyer(e.target.value)}
              />
            </Field>
            <Field
              label="Shipped in"
              hint={
                saleBuyer.trim()
                  ? "Charged once per parcel — other items in this order add nothing"
                  : "Add the buyer above so a bundle is only charged one mailer"
              }
            >
              <select
                value={salePackaging}
                onChange={(e) => setSalePackaging(e.target.value as typeof salePackaging)}
              >
                <option value="poly">
                  Poly mailer{mailer ? ` — ${money(mailer.poly)}` : ""}
                </option>
                <option value="bubble">
                  Bubble mailer{mailer ? ` — ${money(mailer.bubble)}` : ""}
                </option>
                <option value="other">
                  Something else{mailer ? ` — ${money(mailer.other)}` : ""}
                </option>
                <option value="none">No packaging cost</option>
              </select>
            </Field>
          </div>

          {saleTiePlatform === "depop" && (
            <label className="toggle-row">
              <input
                type="checkbox"
                style={{ width: "auto" }}
                checked={saleBoosted}
                onChange={(e) => setSaleBoosted(e.target.checked)}
              />
              <span>
                Sold with Boost
                <span className="hint" style={{ margin: 0 }}>
                  Depop adds {saleFeeProfile?.boostPct ?? 12}% of the item price to its fee
                </span>
              </span>
            </label>
          )}

          {SELLER_SHIPPING_PLATFORMS.includes(saleTiePlatform) && (
            <div className="row">
              <label className="toggle-row">
                <input
                  type="checkbox"
                  style={{ width: "auto" }}
                  checked={salePaysShipping}
                  onChange={(e) => setSalePaysShipping(e.target.checked)}
                />
                <span>
                  I paid for shipping
                  <span className="hint" style={{ margin: 0 }}>
                    Deducted from this sale as a shipping cost
                  </span>
                </span>
              </label>
              {salePaysShipping && (saleTiePlatform === "poshmark" ? (
                <Field label="Shipping" hint="Poshmark's flat rate, or the discounted one">
                  <select
                    value={poshmarkRate(saleShipCost)}
                    onChange={(e) => setSaleShipCost(e.target.value)}
                  >
                    {POSHMARK_SHIPPING.map((v) => (
                      <option key={v} value={v}>{money(Number(v))}</option>
                    ))}
                  </select>
                </Field>
              ) : (
                <Field label="Shipping cost" hint="What the label cost you">
                  <input
                    className="money" type="number" step="0.01" min="0" placeholder="0.00"
                    value={saleShipCost} onChange={(e) => setSaleShipCost(e.target.value)}
                  />
                </Field>
              ))}
            </div>
          )}

          {saleFeePreview && (
            <dl className="record" style={{ marginTop: "0.5rem" }}>
              <div className="record-row">
                <dt>{saleTiePlatform} fee{saleFeePreview.boost > 0 ? " (incl. boost)" : ""}</dt>
                <dd className="num neg">−{money(saleFeePreview.fee)}</dd>
              </div>
              {saleFeePreview.ship > 0 && (
                <div className="record-row">
                  <dt>Shipping you paid</dt>
                  <dd className="num neg">−{money(saleFeePreview.ship)}</dd>
                </div>
              )}
              <div className="record-row">
                <dt>You receive</dt>
                <dd className="num">{money(saleFeePreview.net)}</dd>
              </div>
              {costEnv && (
                <div className="record-row">
                  <dt>Estimated profit</dt>
                  <dd className={`num ${saleFeePreview.profit >= 0 ? "pos" : "neg"}`}>
                    {money(saleFeePreview.profit)}
                  </dd>
                </div>
              )}
            </dl>
          )}

          {saleTieRef && (
            <p className="hint" style={{ fontFamily: "ui-monospace, monospace" }}>
              Order reference: {saleTieRef}
            </p>
          )}

          <div className="actions">
            <button onClick={() => void submitSale()} disabled={busy || !salePrice}>
              {busy ? "Saving" : "Mark sold"}
            </button>
            <button className="quiet" onClick={() => setShowSale(false)} disabled={busy}>Cancel</button>
          </div>
          <p className="hint">
            Marking it sold also ends any active listings for this item.
            {saleTiePlatform === "depop" && " On Depop the buyer covers shipping and tax, so enter only the item price here."}
          </p>
        </section>
      )}

      {showListing && notSold && (
        <section className="rail">
          <h2 className="eyebrow rail-label">Record a listing</h2>
          <div className="row">
            <Field label="Platform">
              <select value={platform} onChange={(e) => setPlatform(e.target.value)}>
                {PLATFORMS.map((p) => (<option key={p} value={p}>{p}</option>))}
              </select>
            </Field>
            <Field label="Asking price">
              <input
                className="money" type="number" step="0.01" min="0"
                value={listPrice} onChange={(e) => setListPrice(e.target.value)}
              />
            </Field>
          </div>
          <div className="actions">
            <button onClick={() => void recordListing().then(() => setShowListing(false))} disabled={busy || !listPrice}>
              {busy ? "Saving" : "Record listing"}
            </button>
            <button className="quiet" onClick={() => setShowListing(false)} disabled={busy}>Cancel</button>
          </div>
          <p className="hint">
            This logs where you listed the item — it does not post to {platform} for you.
          </p>
        </section>
      )}

      <section className="rail">
        <h2 className="eyebrow rail-label">Costs ({item.costs.length})</h2>
        {item.costs.length === 0 ? (
          <p className="hint">
            Nothing recorded against this item yet beyond what you paid for it.
          </p>
        ) : (
          item.costs.map((c) => {
            const editingCost = costDraft[c.id] !== undefined;
            return (
              <div key={c.id} className="cost-row">
                <span className="cost-kind">
                  {c.kind.replace(/_/g, " ")}
                  {c.isEstimate && <span className="cost-estimate">estimate</span>}
                </span>
                {editingCost ? (
                  <>
                    <input
                      className="money" type="number" step="0.01" min="0"
                      value={costDraft[c.id]}
                      onChange={(e) => setCostDraft((d) => ({ ...d, [c.id]: e.target.value }))}
                    />
                    <button onClick={() => void saveCost(c.id)} disabled={busy}>Save</button>
                    <button
                      className="quiet"
                      disabled={busy}
                      onClick={() =>
                        setCostDraft((d) => { const n = { ...d }; delete n[c.id]; return n; })
                      }
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <span className="num cost-amount">{money(c.amount)}</span>
                    <button
                      className="quiet"
                      disabled={busy}
                      onClick={() => setCostDraft((d) => ({ ...d, [c.id]: String(c.amount) }))}
                    >
                      Edit
                    </button>
                    <button className="quiet danger" disabled={busy} onClick={() => void removeCost(c.id)}>
                      Remove
                    </button>
                  </>
                )}
              </div>
            );
          })
        )}
        <p className="hint">
          Packaging is added for you when you mark an item sold — once per parcel, so a bundle
          is only charged one mailer. Anything flagged as an estimate is a guess you can correct
          here, and every margin figure recalculates.
        </p>
      </section>

      <details className="rail photos-details">
        <summary className="rail-summary">Photos ({photoUrls.length}) — tap to add or manage</summary>
        <PhotoManager
          itemId={item.id}
          photos={photoUrls}
          onChange={async () => {
            await load();
            // Only worth offering once the first read has happened.
            if (["complete", "partial", "failed"].includes(item.enrichmentStatus)) setPhotosChanged(true);
          }}
        />
      </details>

      {editing && (
        <section className="rail">
          <h2 className="eyebrow rail-label">Edit record</h2>
          {GROUPS.map((g) => (
            <div key={g} className="edit-grid">
              <h3 className="eyebrow edit-group">{GROUP_TITLE[g]}</h3>

              {g === "origin" && (
                <>
                  <div className="edit-group">
                    <div className="origin-picker" role="radiogroup" aria-label="Where it came from">
                      <button
                        type="button"
                        className={`origin-pick ${draftOrigin === "store" ? "active" : ""}`}
                        aria-pressed={draftOrigin === "store"}
                        onClick={() => setDraftOrigin("store")}
                      >
                        Bought at a store
                      </button>
                      {NO_COST_ORIGINS.map((o) => (
                        <button
                          key={o.name}
                          type="button"
                          className={`origin-pick ${draftOrigin === o.name ? "active" : ""}`}
                          aria-pressed={draftOrigin === o.name}
                          title={o.hint}
                          onClick={() => {
                            setDraftOrigin(o.name);
                            setDraft((d) => ({ ...d, purchasePrice: "0" }));
                          }}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                    <p className="hint" style={{ margin: 0 }}>
                      {draftOrigin === "store"
                        ? "The store drives the by-store breakdown on the dashboard."
                        : `${NO_COST_ORIGINS.find((o) => o.name === draftOrigin)?.hint ?? ""} Still counted as stock, and its sale as profit.`}
                    </p>
                  </div>
                  {draftOrigin === "store" && (
                    <Field label="Store" hint="Where you found it">
                      <input
                        type="text"
                        list="known-stores"
                        placeholder="e.g. Goodwill"
                        value={draftStore}
                        onChange={(e) => setDraftStore(e.target.value)}
                      />
                      <datalist id="known-stores">
                        {stores.filter((s) => !isNoCostOrigin(s)).map((s) => <option key={s} value={s} />)}
                      </datalist>
                    </Field>
                  )}
                </>
              )}

              {EDITABLE.filter((f) => f.group === g).map((f) => {
                const noCostPrice = f.key === "purchasePrice" && draftOrigin !== "store";
                return (
                  <Field
                    key={f.key}
                    label={f.label}
                    hint={noCostPrice ? "No cost basis for this origin" : undefined}
                  >
                    {f.type === "select" ? (
                      <select
                        value={draft[f.key] ?? ""}
                        onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                      >
                        {(f.options ?? []).map((o) => (
                          <option key={o} value={o}>
                            {o === "" ? "—" : (f.optionLabel?.[o] ?? o)}
                          </option>
                        ))}
                      </select>
                    ) : f.type === "textarea" ? (
                      <textarea
                        rows={f.key === "listingDescription" ? 10 : 3}
                        value={draft[f.key] ?? ""}
                        onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                      />
                    ) : (
                      <input
                        type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                        step={f.type === "number" ? "0.01" : undefined}
                        disabled={noCostPrice}
                        value={noCostPrice ? "0" : (draft[f.key] ?? "")}
                        onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                      />
                    )}
                  </Field>
                );
              })}

              {g === "origin" && (
                <label className="toggle-row edit-group">
                  <input
                    type="checkbox"
                    style={{ width: "auto" }}
                    checked={draftPersonal}
                    onChange={(e) => setDraftPersonal(e.target.checked)}
                  />
                  <span>
                    Keeping it — not for resale
                    <span className="hint" style={{ margin: 0 }}>
                      Left out of capital spent, inventory value and profit. Different from
                      "our own closet", which is stock you are selling.
                    </span>
                  </span>
                </label>
              )}
            </div>
          ))}

          <div className="actions">
            <button onClick={() => void saveEdit()} disabled={busy}>
              {busy ? "Saving" : "Save changes"}
            </button>
            <button className="quiet" onClick={() => setEditing(false)} disabled={busy}>
              Cancel
            </button>
          </div>
        </section>
      )}

      <section className="rail">
        <h2 className="eyebrow rail-label">Money</h2>
        <div className="ledger">
          <div>
            <span className="eyebrow">Tag price</span>
            <span className="value">{money(item.tagPrice)}</span>
          </div>
          <div>
            <span className="eyebrow">You paid</span>
            <span className="value">{money(item.purchasePrice)}</span>
          </div>
          {standingSale && (
            <div>
              <span className="eyebrow">Sold for</span>
              <span className="value">{money(standingSale.salePrice)}</span>
              <span className="sold-on">
                <PlatformMark platform={standingSale.platform} size={18} />
                <span>{standingSale.platform} · {shortDate(standingSale.soldAt)}</span>
              </span>
            </div>
          )}
          <div>
            <span className="eyebrow">
              {realizedProfit != null ? "Net profit" : "Est. avg profit"}
            </span>
            {realizedProfit != null ? (
              <span className={`value ${realizedProfit >= 0 ? "pos" : "neg"}`}>
                {money(realizedProfit)}
              </span>
            ) : estAvgProfit != null ? (
              <span
                className={`value ${estAvgProfit >= 0 ? "pos" : "neg"}`}
                title={`Average across ${sellingFees.length} sites at ${money(refPrice)}`}
              >
                {money(estAvgProfit)}
              </span>
            ) : (
              <span className="value">—</span>
            )}
          </div>
          <div>
            <span className="eyebrow">Days held</span>
            <span className="value">{item.ledger?.daysHeld ?? "—"}</span>
          </div>
        </div>
      </section>

      {!item.isPersonal && <CrosslistPanel itemId={item.id} onChange={() => void load()} />}

      {item.listings.length > 0 && (
        <section className="rail">
          <h2 className="eyebrow rail-label">Where it's listed</h2>
          {item.listings.map((l) => (
            <div key={l.id} className="tag" style={{ cursor: "default" }}>
              <PlatformMark platform={l.platform} />
              <span className="tag-body">
                <span className="tag-title" style={{ textTransform: "capitalize" }}>{l.platform}</span>
                <span className="tag-meta">
                  <Sticker status={l.status} />
                  {l.externalUrl && (
                    <a href={l.externalUrl} target="_blank" rel="noreferrer">Open listing</a>
                  )}
                </span>
                <span className="row-actions">
                  <button className="quiet" onClick={() => void editListingPrice(l.id, l.listPrice)} disabled={busy}>
                    Edit price
                  </button>
                  <button className="quiet" onClick={() => void archiveListing(l.id)} disabled={busy}>
                    Archive
                  </button>
                  <button className="quiet danger" onClick={() => void deleteListing(l.id)} disabled={busy}>
                    Delete
                  </button>
                </span>
              </span>
              <span className="tag-price">
                <span className="ask">{money(l.listPrice)}</span>
                {(() => {
                  const f = feeFor(l.platform);
                  if (!basis || !f || !l.listPrice) return null;
                  const est = estimateProfit(Number(l.listPrice), f, basis);
                  return (
                    <span className={`listing-profit ${est >= 0 ? "pos" : "neg"}`}>
                      est. {money(est)}
                    </span>
                  );
                })()}
              </span>
            </div>
          ))}
        </section>
      )}

      {item.comps.length > 0 && (
        <section className="rail">
          <h2 className="eyebrow rail-label">Comparable listings</h2>
          {item.comps.slice(0, 8).map((c, i) => (
            <div key={i} className="tag" style={{ cursor: "default" }}>
              <span className="tag-thumb" aria-hidden="true" />
              <span className="tag-body">
                <span className="tag-title">
                  <a href={c.url} target="_blank" rel="noreferrer">{c.title}</a>
                </span>
                <span className="tag-meta">
                  {c.soldAt ? `sold ${new Date(c.soldAt).toLocaleDateString()}` : "active listing"}
                </span>
              </span>
              <span className="tag-price"><span className="ask">{money(c.price)}</span></span>
            </div>
          ))}
        </section>
      )}

      <section className="rail">
        <h2 className="eyebrow rail-label">Everything on record</h2>
        <dl className="record">
          {RECORD_FIELDS.map(({ key, label, ai }) => (
            <div key={key} className="record-row">
              <dt>
                {label}
                {ai && manualFields.has(key)
                  ? <span className="manual-badge" title="Set by hand — a re-read of the photos keeps this value">pinned</span>
                  : ai && aiDerived && <span className="ai-badge">✨ AI</span>}
              </dt>
              <dd>
                {key === "conditionGrade"
                  ? (conditionLabel(item.conditionGrade) ?? "—")
                  : key === "audience"
                    ? (audienceLabel(item.audience) ?? "—")
                    : key === "createdAt" || key === "updatedAt"
                      ? fmtEastern(fieldValue(item, key))
                      : key === "listingDescription"
                        ? <span style={{ whiteSpace: "pre-wrap" }}>{fmtValue(fieldValue(item, key))}</span>
                        : fmtValue(fieldValue(item, key))}
              </dd>
            </div>
          ))}
        </dl>
        {(() => {
          const raw = item as unknown as Record<string, unknown>;
          const meta: Record<string, unknown> = {};
          for (const k of ["attributes", "measurements"]) {
            const v = raw[k];
            if (v && typeof v === "object" && Object.keys(v as object).length > 0) meta[k] = v;
          }
          if (Object.keys(meta).length === 0) return null;
          return (
            <>
              <h3 className="eyebrow" style={{ marginTop: "1.25rem", color: "var(--muted)" }}>
                Metadata
              </h3>
              <pre className="record-json">{JSON.stringify(meta, null, 2)}</pre>
            </>
          );
        })()}
      </section>

      <div className="actions">
        <button className="quiet" onClick={() => navigate("/")}>Back to inventory</button>
      </div>
    </div>
  );
}
